import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { NovaAgencia } from "@/components/NovaAgencia";
import { CmiMinutasAdmin } from "@/components/CmiMinutasAdmin";
import { LegalDefinicoes } from "@/components/LegalDefinicoes";
import { RulesTests, FalseAlarms } from "@/components/RulesQuality";
import { ReadQuality } from "@/components/AutoAccept";
import { PlatformRules } from "@/components/PlatformRules";
import { PilotBoard } from "@/components/PilotBoard";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";
const btn = "bg-ec-green text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-ec-greenDark transition-colors disabled:opacity-50";

function AgencyRow({ a, domain, onDone, templates }) {
  const [slug, setSlug] = useState(a.slug);
  const save = async () => {
    try { await api.put(`/platform/agencies/${a.id}/slug`, { slug }); toast.success("Endereço guardado"); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const wl = async (white_label) => { await api.put(`/platform/agencies/${a.id}/white-label`, { white_label }); onDone(); };
  const cmi = async (template) => {
    try { await api.put(`/platform/agencies/${a.id}/cmi-template`, { template: template === "none" ? null : template }); toast.success("Modelo de CMI guardado"); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <li className="py-4 border-t border-slate-100 first:border-t-0 grid md:grid-cols-[1fr_auto_auto_auto] gap-3 md:items-center" data-testid={`admin-agency-${a.slug || a.id}`}>
      <div>
        <div className="font-semibold text-ec-green">{a.name} {a.is_demo && <span className="ml-1 text-[11px] font-semibold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">Demonstração</span>}</div>
        <div className="text-sm text-slate-500">{a.users} utilizador{a.users === 1 ? "" : "es"}</div>
      </div>
      <div className="flex items-center gap-2">
        <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="w-36 h-9" data-testid={`admin-slug-${a.id}`} />
        <span className="text-xs text-slate-400 hidden lg:inline">@{domain}</span>
        <button onClick={save} disabled={slug === a.slug} className={btn} data-testid={`admin-slug-save-${a.id}`}>Guardar</button>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600">Marca branca <Switch checked={a.white_label} onCheckedChange={wl} data-testid={`admin-wl-${a.id}`} /></label>
      <label className="flex items-center gap-2 text-sm text-slate-600">Modelo de CMI
        <select value={a.cmi_template || "none"} onChange={(e) => cmi(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`admin-cmi-${a.id}`}>
          <option value="none">Sem modelo</option>
          {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </label>
    </li>
  );
}

function Agencias({ domain, list, reload }) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/platform/cmi-templates").then(({ data }) => setTemplates(data)).catch(() => {}); }, []);
  return (
    <div className={card} data-testid="admin-agencies">
      <h3 className="font-heading text-lg text-ec-green">Agências · {list.length}</h3>
      <p className="text-sm text-slate-500">O endereço (slug) define o email de receção: slug@{domain} e, por processo, slug+nº@{domain}.</p>
      <ul className="mt-3">{list.map((a) => <AgencyRow key={a.id} a={a} domain={domain} onDone={reload} templates={templates} />)}</ul>
    </div>
  );
}

function Rececao({ cfg, setCfg }) {
  const [d, setD] = useState(cfg);
  const save = async () => {
    try { const { data } = await api.put("/platform/inbound", d); setCfg(data); setD(data); toast.success("Receção de email guardada"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className={card} data-testid="admin-inbound">
      <h3 className="font-heading text-lg text-ec-green">Receção de documentos por email</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-2xl">Aponte o registo MX do domínio para o serviço de receção (SendGrid Inbound Parse) com o endereço <span className="font-mono text-xs">{process.env.REACT_APP_BACKEND_URL}/api/inbound/sendgrid</span>. Enquanto não estiver marcado como configurado, os endereços não aparecem às agências nem aos clientes.</p>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1"><Label htmlFor="in-domain">Domínio</Label><Input id="in-domain" value={d.domain} onChange={(e) => setD({ ...d, domain: e.target.value })} className="mt-1.5" data-testid="admin-inbound-domain" /></div>
        <label className="flex items-center gap-2 text-sm text-slate-600 h-10">Domínio configurado <Switch checked={d.configured} onCheckedChange={(configured) => setD({ ...d, configured })} data-testid="admin-inbound-configured" /></label>
        <button onClick={save} className={btn + " h-10"} data-testid="admin-inbound-save">Guardar</button>
      </div>
    </div>
  );
}

function Simular({ domain, list }) {
  const [f, setF] = useState({ to: list[0]?.slug ? `${list[0].slug}@${domain}` : "", from: "", subject: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const files = useRef();
  const send = async () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    [...(files.current?.files || [])].forEach((x, i) => fd.append(`attachment${i + 1}`, x));
    setBusy(true);
    try { const { data } = await api.post("/platform/inbound/simulate", fd); setRes(data); toast.success("Email simulado processado"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const field = (k, label, ph) => (
    <div><Label htmlFor={`sim-${k}`}>{label}</Label><Input id={`sim-${k}`} value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5" data-testid={`sim-${k}`} /></div>
  );
  return (
    <div className={card} data-testid="admin-simulate">
      <h3 className="font-heading text-lg text-ec-green">Simular email recebido</h3>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {field("to", "Para", `kwselect+15@${domain}`)}
        {field("from", "De", "cliente@email.pt")}
        <div className="sm:col-span-2">{field("subject", "Assunto", "Documentos")}</div>
        <div className="sm:col-span-2"><Label htmlFor="sim-text">Texto</Label><Textarea id="sim-text" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className="mt-1.5" data-testid="sim-text" /></div>
        <div className="sm:col-span-2"><Label>Anexos</Label><input ref={files} type="file" multiple className="block mt-1.5 text-sm" data-testid="sim-files" /></div>
      </div>
      <button onClick={send} disabled={busy || !f.to} className={btn + " mt-5"} data-testid="sim-send">{busy ? "A processar…" : "Simular receção"}</button>
      {res && (
        <div className="mt-4 text-sm rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid="sim-result">
          {res.status === "associado" ? `Associado ao processo ${res.matched_ref}` : `Por classificar${res.matched_ref ? ` (processo ${res.matched_ref}, remetente desconhecido)` : ""}`}
          {" · "}{res.attachments.length} anexo(s){res.ignored.length ? ` · ${res.ignored.length} ignorado(s)` : ""}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [list, setList] = useState([]);
  const reload = () => api.get("/platform/agencies").then(({ data }) => setList(data));
  useEffect(() => { api.get("/platform/inbound").then(({ data }) => setCfg(data)); reload(); }, []);
  return (
    <div className="min-h-screen bg-ec-bg font-body" data-testid="admin-page">
      <header className="bg-[#13224A] text-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0"><Logo dark size={28} /><span className="hidden sm:inline text-sm text-white/70">Administração da plataforma</span></div>
          <div className="flex items-center gap-3 text-sm"><span className="hidden sm:inline text-white/80">{user.name}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20" data-testid="admin-logout"><LogOut size={14} /> Sair</button></div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-10 space-y-6">
        <h1 className="font-heading text-3xl md:text-4xl text-ec-green">Administração</h1>
        <PilotBoard />
        {cfg && <Agencias domain={cfg.domain} list={list} reload={reload} />}
        <NovaAgencia onCreated={reload} />
        <CmiMinutasAdmin />
        {cfg && <Rececao cfg={cfg} setCfg={setCfg} />}
        {cfg && list.length > 0 && <Simular domain={cfg.domain} list={list} />}
        <LegalDefinicoes />
        <RulesTests />
        <FalseAlarms />
        <ReadQuality />
        <PlatformRules />
      </main>
    </div>
  );
}
